/* global dataLayer */

import { graphql } from 'gatsby';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import c from './../utils/constants';
import Container from './../components/container';
import Header from './../components/header/header';
import SubHeader from './../components/header/sub-header';
import { navigate } from 'gatsby';
import * as utilFormat from './../utils/format';
import { Helmet } from 'react-helmet';
import * as utilAwards from './../utils/awards';
import Layout from '../components/layout';

export default class LocationAwardTemplate extends Component {
  static propTypes = {
    data: PropTypes.object,
    location: PropTypes.object,
    pageContext: PropTypes.shape({
      year: PropTypes.string.isRequired,
      // location to display to user. e.g. 'United States'
      locationName: PropTypes.string.isRequired,
      // location for generating routes. e.g. 'united_states'
      locationSlug: PropTypes.string.isRequired
    })
  };

  constructor(props) {
    super(props);
    this.state = {
      search: '',
      hasMounted: false
    };
  }

  componentDidMount() {
    const {
      location: { search, pathname }
    } = this.props;

    this.setState({ hasMounted: true });
    const pageTitle = this.getHeaderData().title;

    // send event to GTM
    typeof dataLayer !== 'undefined' &&
      dataLayer.push({
        pageTitle,
        virtualUrl: `${pathname}${search}`,
        event: 'virtualPageview'
      });
  }

  handleSearchChange = (search) => {
    this.setState({
      search
    });
  };

  onDatepickerSelect = (selectedValue) => {
    const {
      pageContext: { locationSlug }
    } = this.props;

    const { year, quarterRange } = utilFormat.parseDatepickerOption(selectedValue);
    const route = utilFormat.getLocationAwardRoute(locationSlug, year, quarterRange);
    navigate(route);
  };

  getSelectedDatepickerOption = () => {
    const {
      location: { search },
      pageContext: { year }
    } = this.props;

    const timePeriod = qs.parse(search, { ignoreQueryPrefix: true }).time_period;
    return utilFormat.getDatepickerOption(year, timePeriod);
  };

  getSingleAwardHeaderData = (timePeriod, locationName, companyName, awardType, year) => {
    // Title: [Country Name] – [Company] [Year] [Award Type] Speedtest Award
    // Meta: [Company] is [Country Name]’s Speedtest Awards Winner for [Award Type] during [Time Period (quarters)] [Year]
    return {
      title: `${locationName} – ${companyName} ${year} ${c.awardTypes[awardType].label} Speedtest Award`,
      desc: `${companyName} is ${locationName}’s Speedtest Awards Winner for ${
        c.awardTypes[awardType].label
      } during ${timePeriod.toUpperCase()} ${year}`
    };
  };

  /**
   * Generate title and meta description for page
   * @param  {Object} options      optional
   * @param  {String} locationName required
   * @param  {Numer}  year         required
   * @return {Object}              object with page title and meta description
   */
  getHeaderData = (locationName, year, options = {}) => {
    const { timePeriod, awardType, companyName } = options;

    if (awardType && timePeriod && companyName) {
      return this.getSingleAwardHeaderData(timePeriod, locationName, companyName, awardType, year);
    } else if (timePeriod) {
      // Title: [Country Name] – [Qx–Qx] [Year] Speedtest Awards
      // Meta: Speedtest Awards won in [Country] in [Qx–Qx] [Year]
      return {
        title: `${locationName} – ${timePeriod.toUpperCase()} ${year} Speedtest Awards`,
        desc: `Speedtest Awards won in ${locationName} in ${timePeriod.toUpperCase()} ${year}`
      };
    }

    // Title: [Country Name] – [Year] Speedtest Awards
    // Meta: A full list of the Speedtest Awards won in [Country] in [Year]
    return {
      title: `${locationName} – ${year} Speedtest Awards`,
      desc: `A full list of the Speedtest Awards won in ${locationName} in ${year}`
    };
  };

  groupSectionsAndNavItems(filteredAndSortedAwards, timePeriod, groupBy) {
    const sectionOrder = groupBy === 'timePeriodAndType' ? { type: 'auto', timePeriod: c.timePeriods } : 'auto';
    const sections = utilAwards.groupAwards(filteredAndSortedAwards, groupBy, sectionOrder);
    const navItems = utilAwards.convertSectionsToNavItems(sections, groupBy, 'winner', 'location');

    return {
      sections,
      navItems
    };
  }

  generateEdgesAndDatePicker = (data) => {
    const edges = [];
    const datePicker = [];

    Object.keys(data).forEach((key) => {
      if (key.indexOf('allYear') > -1) {data[key].edges.map((edge) => (edge.node.winner ? edges.push(edge) : null));}
      if (key.indexOf('datePicker') > -1) {
        datePicker.push({
          year: parseInt(key.split('datePicker')[1]),
          edges: data[key].edges.filter((edge) => edge.node.timePeriod)
        });
      }
    });
    return { edges, datePicker };
  };

  render() {
    const {
      pageContext: { locationName, locationSlug, year },
      location: { search }
    } = this.props;
    const edgesAndDatePicker = this.generateEdgesAndDatePicker(this.props.data);

    const awardType = qs.parse(search, { ignoreQueryPrefix: true }).award_type;
    const timePeriod = qs.parse(search, { ignoreQueryPrefix: true }).time_period;

    const rawAwards = utilAwards.convertEdgesToAwards(edgesAndDatePicker.edges);
    const filteredAwards = utilAwards.filterAwards(rawAwards, { awardType, timePeriod });
    const filteredAndSortedAwards = utilAwards.sortByMostRecentDateSold(filteredAwards);

    const groupBy = timePeriod ? 'type' : 'timePeriodAndType';

    // Based on the business logic that only one award of a given type can be won in a given timePeriod,
    // we should expect that filteredAndSortedAwards will be an array with a single element.
    const singleAward = timePeriod && awardType ? filteredAndSortedAwards[0] : undefined;

    // If rendering a single award there is no need to group awards or navItems
    const { sections, navItems } = !singleAward
      ? this.groupSectionsAndNavItems(filteredAndSortedAwards, timePeriod, groupBy)
      : {};

    const methodologyLink = utilAwards.computeMethodologyLink(filteredAndSortedAwards, c.methodologyLink);

    const headerData = this.getHeaderData(locationName, year, {
      timePeriod,
      awardType,
      companyName: singleAward ? singleAward.winner : undefined
    });
    if (!this.state.hasMounted) { return null; }
    return (
      <Layout>
        <div>
          <Helmet>
            <html lang="en" />
            <meta name="description" content={headerData.desc} />
            <title>{headerData.title}</title>
          </Helmet>
          <div className="section section-dark section-blue section-md">
            <div className="container">
              <Header
                onSearchChange={this.handleSearchChange}
                searchData={utilFormat.edgesToSearchData(this.props.data.search.edges)}
                search={this.state.search}
              />
              <SubHeader title={locationName} timePeriod={`${timePeriod ? timePeriod : ''} ${year}`} />
              <Container
                handleDatepickerSelect={this.onDatepickerSelect}
                locationName={locationName}
                locationSlug={locationSlug}
                template="location"
                sections={sections}
                singleAward={singleAward}
                groupedBy={groupBy}
                navItems={navItems}
                year={year}
                awardType={awardType}
                datepickerData={edgesAndDatePicker.datePicker}
                timePeriod={timePeriod}
                methodologyLink={methodologyLink}
                datepickerSelectedOption={this.getSelectedDatepickerOption()}
              />
            </div>
          </div>
        </div>
      </Layout>
    );
  }
}

export const query = graphql`
  query LocationAwardQuery($locationAwardSlug: String!, $datePickerFilter: String!) {
    search: allSitePage {
      ...Search
    }
    datePicker2017: allYear2017Json(filter: { locationName: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2018: allYear2018Json(filter: { locationName: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2019: allYear2019Json(filter: { locationName: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2020: allYear2020Json(filter: { locationName: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2021: allYear2021Json(filter: { locationName: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2022: allYear2022Json(filter: { locationName: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2023: allYear2023Json(filter: { locationName: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2024: allYear2024Json(filter: { locationName: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    datePicker2025: allYear2025Json(filter: { locationName: { eq: $datePickerFilter } }) {
      edges {
        node {
          timePeriod
        }
      }
    }
    allYear2017Json(filter: { fields: { locationAwardSlug: { eq: $locationAwardSlug } } }) {
      edges {
        node {
          awardImage
          methodologyLink
          locationName
          year
          dateSold
          type
          winner
          timePeriod
          type
          location {
            country
            countryId
            locationCopy
            type
          }
          report {
            link
            text
          }
          providerCopy
          data {
            tooltips {
              speedScore
            }
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              download_speed
              upload_speed
              rating
            }
          }
        }
      }
    }
    allYear2018Json(filter: { fields: { locationAwardSlug: { eq: $locationAwardSlug } } }) {
      edges {
        node {
          awardImage
          methodologyLink
          locationName
          year
          dateSold
          type
          winner
          timePeriod
          type
          location {
            country
            countryId
            locationCopy
            type
          }
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            tooltips {
              speedScore
            }
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              rating
            }
          }
        }
      }
    }
    allYear2019Json(filter: { fields: { locationAwardSlug: { eq: $locationAwardSlug } } }) {
      edges {
        node {
          awardImage
          methodologyLink
          locationName
          year
          dateSold
          type
          winner
          timePeriod
          type
          location {
            country
            countryId
            locationCopy
            type
          }
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            tooltips {
              speedScore
            }
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              rating
            }
          }
        }
      }
    }
    allYear2020Json(filter: { fields: { locationAwardSlug: { eq: $locationAwardSlug } } }) {
      edges {
        node {
          awardImage
          methodologyLink
          locationName
          year
          dateSold
          type
          winner
          timePeriod
          type
          location {
            country
            countryId
            locationCopy
            type
          }
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            tooltips {
              speedScore
            }
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              rating
            }
          }
        }
      }
    }
    allYear2021Json(filter: { fields: { locationAwardSlug: { eq: $locationAwardSlug } } }) {
      edges {
        node {
          awardImage
          methodologyLink
          locationName
          year
          dateSold
          type
          winner
          timePeriod
          type
          location {
            country
            countryId
            locationCopy
            type
          }
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            tooltips {
              speedScore
            }
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              video_score
              video_name
              rating
            }
          }
        }
      }
    }
    allYear2022Json(filter: { fields: { locationAwardSlug: { eq: $locationAwardSlug } } }) {
      edges {
        node {
          awardImage
          methodologyLink
          locationName
          year
          dateSold
          type
          winner
          timePeriod
          type
          location {
            country
            countryId
            locationCopy
            type
          }
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            tooltips {
              speedScore
            }
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              video_score
              video_name
              rating
            }
          }
        }
      }
    }
    allYear2023Json(filter: { fields: { locationAwardSlug: { eq: $locationAwardSlug } } }) {
      edges {
        node {
          awardImage
          methodologyLink
          locationName
          year
          dateSold
          type
          winner
          timePeriod
          type
          location {
            country
            countryId
            locationCopy
            type
          }
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            tooltips {
              speedScore
            }
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              video_score
              game_score
              best_5g_gaming_experience_name
              best_mobile_gaming_experience_name
              best_isp_gaming_experience_name
              video_name
              rating
            }
          }
        }
      }
    }
    allYear2024Json(filter: { fields: { locationAwardSlug: { eq: $locationAwardSlug } } }) {
      edges {
        node {
          awardImage
          methodologyLink
          locationName
          year
          dateSold
          type
          winner
          timePeriod
          type
          location {
            country
            countryId
            locationCopy
            type
          }
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            tooltips {
              speedScore
            }
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              video_score
              game_score
              best_5g_gaming_experience_name
              best_mobile_gaming_experience_name
              best_isp_gaming_experience_name
              video_name
              rating
              overall_connectivity_score
              best_fixed_name
            }
          }
        }
      }
    }
    allYear2025Json(filter: { fields: { locationAwardSlug: { eq: $locationAwardSlug } } }) {
      edges {
        node {
          awardImage
          methodologyLink
          locationName
          year
          dateSold
          type
          winner
          timePeriod
          type
          location {
            country
            countryId
            locationCopy
            type
          }
          report {
            link
            text
          }
          report2 {
            link
            text
          }
          report3 {
            link
            text
          }
          providerCopy
          data {
            tooltips {
              speedScore
            }
            metric
            rankings {
              country_id
              type
              isp_id
              isp_name
              best_name
              speed_score
              mobile_carrier_id
              carrier_name
              top_rated_name
              download_speed
              upload_speed
              coverage_score
              coverage_name
              video_score
              game_score
              best_5g_gaming_experience_name
              best_mobile_gaming_experience_name
              best_isp_gaming_experience_name
              video_name
              rating
              overall_connectivity_score
              best_fixed_name
              best_5g_name
              best_mobile_name
            }
          }
        }
      }
    }
  }
`;
